import { useCallback, useEffect, useState } from "react";
import { apiCall, ApiError } from "@/lib/api";
import type { SimulationListResponse, SimulationSummary } from "@/types/simulation";

type Role = SimulationListResponse["role"];

interface UseSimulationsState {
  loading: boolean;
  error: string | null;
  simulations: SimulationSummary[];
  role: Role | null;
  reload: () => void;
}

export function useSimulations(): UseSimulationsState {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [simulations, setSimulations] = useState<SimulationSummary[]>([]);
  const [role, setRole] = useState<Role | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    // 120s + one silent retry on abort — covers the Azure Functions cold-start
    // window. Without this, the 25s default trips before the function spins up
    // and surfaces "signal is aborted without reason" to the learner on the
    // very first visit of the session.
    const call = (): Promise<SimulationListResponse> =>
      apiCall<SimulationListResponse>("/simulations", { timeoutMs: 120_000 });
    try {
      let data: SimulationListResponse;
      try {
        data = await call();
      } catch (e) {
        const isAbort = e instanceof Error && /abort/i.test(e.message);
        if (!isAbort) throw e;
        await new Promise((r) => setTimeout(r, 1000));
        data = await call();
      }
      setSimulations(data.items);
      setRole(data.role);
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Failed to load simulations";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { loading, error, simulations, role, reload: () => void load() };
}
